import React, { useEffect, useRef, useState } from "react";
import { BsChevronDown, BsPlus } from "react-icons/bs";
import { Button, Switch } from "@nextui-org/react";
import { IoSearch } from "react-icons/io5";
import { useTranslation } from "react-i18next";

import MFloatingComponent from "../MFloatingComponent";

import { normalizeForSearch } from "../../../utils/stringUtils";

// Text used to match an item against the search query. Falls back to the label
// when it is a plain string, so call sites with rich labels can pass
// `searchLabel` explicitly.
const getItemSearchText = (item) =>
  item?.searchLabel ?? (typeof item?.label === "string" ? item.label : "");

const MHoverToggleMenu = ({
  items,
  setSelectedId,
  selectedId,
  canCreate = false,
  onCreate,
  triggerComponent,
  isTopPositioned = false,
  preventToggle = false,
  wrapperClassNames = "",
  floatingWrapperClassNames = "",
  isMultiSelect = false,
  selectedIds = [],
  customRender,
  // true forces the search field, false hides it, "auto" shows it only once the
  // list is long enough to be tedious to scan.
  searchable = "auto",
  searchThreshold = 7,
  searchPlaceholder = "Search",
}) => {
  const { t } = useTranslation();
  const [isFloatingMenuDisplayed, setIsFloatingMenuDisplayed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  const safeItems = items || [];
  const isSearchDisplayed =
    searchable === true || (searchable === "auto" && safeItems.length >= searchThreshold);

  const normalizedQuery = normalizeForSearch(searchQuery);
  const filteredItems =
    isSearchDisplayed && normalizedQuery
      ? safeItems.filter((item) =>
          normalizeForSearch(getItemSearchText(item)).includes(normalizedQuery)
        )
      : safeItems;

  // Drop the query when the menu closes so it reopens on the full list.
  useEffect(() => {
    if (!isFloatingMenuDisplayed) {
      setSearchQuery("");
      return;
    }
    if (isSearchDisplayed) {
      searchInputRef.current?.focus();
    }
  }, [isFloatingMenuDisplayed, isSearchDisplayed]);

  const onToggleItem = (itemKey) => {
    // if (selectedId === itemKey) {
    //   setSelectedId(null);
    //   return;
    // }
    setSelectedId(itemKey);
    // Multi-select menus stay open (and keep the query) so several items can be
    // picked in a row; single-select ones close on pick as before.
    if (!isMultiSelect) {
      setIsFloatingMenuDisplayed(false);
    }
  };

  const onToggleFloatingMenu = () => {
    if (!isFloatingMenuDisplayed && preventToggle) {
      return;
    }

    setIsFloatingMenuDisplayed(!isFloatingMenuDisplayed);
  };

  const onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (searchQuery) {
        setSearchQuery("");
      } else {
        setIsFloatingMenuDisplayed(false);
      }
      return;
    }

    if (event.key === "Enter" && filteredItems.length > 0) {
      event.preventDefault();
      onToggleItem(filteredItems[0].key);
    }
  };

  const renderTrigger = () => (
    <div
      onClick={onToggleFloatingMenu}
      onKeyDown={onToggleFloatingMenu}
    >
      {triggerComponent}
    </div>
  );

  const renderSearchField = () => (
    <div className="relative mb-1.5">
      <IoSearch
        size={14}
        color="#737373"
        className="absolute left-[8px] top-1/2 -translate-y-1/2 pointer-events-none"
      />
      <input
        ref={searchInputRef}
        type="text"
        value={searchQuery}
        placeholder={t(searchPlaceholder)}
        onChange={(event) => setSearchQuery(event.target.value)}
        onKeyDown={onSearchKeyDown}
        className="w-full text-sm rounded-md border-1 border-solid border-[#E5E5E5] bg-white py-1 pl-[28px] pr-[8px] outline-none focus:border-primary"
      />
    </div>
  );

  const renderFLoatingMenu = () => (
    <div
      className={`mt-1.5 bg-white rounded-lg p-2 text-[#33403F] drop-shadow-sm ${
        isSearchDisplayed ? "min-w-[200px]" : ""
      }`}
    >
      {isSearchDisplayed && renderSearchField()}
      <div className="max-h-[260px] overflow-y-auto">
        {filteredItems.length === 0 ? (
          <div className="py-1.5 px-1 text-sm text-[#737373] whitespace-nowrap">
            {t("No result")}
          </div>
        ) : (
          filteredItems.map((item) => {
            const isSelected = isMultiSelect
              ? selectedIds.includes(item.key)
              : selectedId === item.key;

            return (
              <div
                key={item.key}
                className={`flex items-center py-1.5 hover:bg-black/10 rounded-md min-w-[136px] cursor-pointer ${
                  isSelected ? "bg-black/10" : ""
                }`}
                onClick={() => onToggleItem(item.key)}
                onKeyDown={() => onToggleItem(item.key)}
              >
                {/* <Switch
                  isSelected={isSelected}
                  onValueChange={() => onToggleItem(item.key)}
                /> */}
                <span className="ml-1 text-sm whitespace-nowrap">{item.label}</span>
              </div>
            );
          })
        )}
      </div>
      {customRender && customRender}
    </div>
  );

  return (
    <div className={`flex items-center ${wrapperClassNames}`}>
      <MFloatingComponent
        wrapperClassNames={floatingWrapperClassNames}
        isTopPositioned={isTopPositioned}
        targetElement={renderTrigger()}
        floatingElement={renderFLoatingMenu()}
        isFloatingDisplayed={isFloatingMenuDisplayed}
        setIsFloatingDisplayed={setIsFloatingMenuDisplayed}
      />
    </div>
  );
};

export default MHoverToggleMenu;
